import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

@customElement('jv-button')
export class JvButton extends LitElement {
  static styles = css`
    :host {
      display: inline-block;
      box-sizing: border-box;
      max-width: 100%;
    }
    *, *::before, *::after {
      box-sizing: border-box;
    }
    a,
    button {
      box-sizing: border-box;
      max-width: 100%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      min-height: 2.6rem;
      padding: 0.55rem 1.15rem;
      border-radius: var(--jv-radius);
      border: var(--jv-border-w) solid var(--jv-color-border);
      font-family: var(--jv-font-body);
      font-size: var(--jv-fs-sm);
      font-weight: var(--jv-fw-medium);
      text-transform: uppercase;
      letter-spacing: 0.03em;
      cursor: pointer;
      text-decoration: none;
      background: var(--jv-color-accent);
      color: var(--jv-color-on-accent);
      box-shadow: var(--jv-shadow);
      transition: transform 0.08s ease, box-shadow 0.08s ease, background 0.15s ease;
    }
    a:hover,
    button:hover:not(:disabled) {
      background: var(--jv-color-accent-hover);
    }
    a:active,
    button:active:not(:disabled) {
      transform: translate(3px, 3px);
      box-shadow: var(--jv-shadow-press);
    }
    :host([variant='ghost']) a,
    :host([variant='ghost']) button {
      background: var(--jv-color-surface);
      color: var(--jv-color-text);
    }
    :host([variant='ghost']) a:hover,
    :host([variant='ghost']) button:hover:not(:disabled) {
      background: var(--jv-color-tint);
    }
    :host([variant='subtle']) a,
    :host([variant='subtle']) button {
      background: var(--jv-color-accent-soft);
      color: var(--jv-color-accent-hover);
    }
    :host([variant='amber']) a,
    :host([variant='amber']) button {
      background: var(--jv-color-amber);
      color: var(--jv-color-ink);
    }
    :host([variant='amber']) a:hover,
    :host([variant='amber']) button:hover:not(:disabled) {
      background: var(--jv-color-amber-hover);
    }
    :host([size='small']) a,
    :host([size='small']) button {
      min-height: 2rem;
      padding: 0.3rem 0.7rem;
      font-size: var(--jv-fs-xs);
      box-shadow: none;
    }
    :host([block]) {
      display: block;
      width: 100%;
    }
    :host([block]) a,
    :host([block]) button {
      width: calc(100% - 4px);
    }
    button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `;

  @property({ reflect: true }) variant: 'primary' | 'ghost' | 'subtle' | 'amber' = 'primary';
  @property({ reflect: true }) size: 'default' | 'small' = 'default';
  @property() href?: string;
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean, reflect: true }) block = false;
  @property() type: 'button' | 'submit' = 'button';

  render() {
    const inner = html`<slot></slot>`;
    if (this.href && !this.disabled) {
      return html`<a href=${this.href}>${inner}</a>`;
    }
    return html`<button type="button" ?disabled=${this.disabled} @click=${this.onClick}>
      ${inner}
    </button>`;
  }

  private onClick = () => {
    if (this.disabled) return;
    if (this.type === 'submit') {
      this.closest('form')?.requestSubmit();
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'jv-button': JvButton;
  }
}
